import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Wifi, Users, CheckCircle, MapPin, CalendarCheck, Mail } from "lucide-react";
import { PageHeader } from "@/components/site/PageHeader";
import { Seo } from "@/components/site/Seo";
import { CONTACT } from "@/data/site";

const PHOTOS = [
  { src: "/coworking/sala.jpg", alt: "Open space della sede treffe con scrivanie e sedie" },
  { src: "/coworking/aula.jpg", alt: "Aula treffe con sedie e logo a parete" },
  { src: "/coworking/vetrata.jpg", alt: "Postazioni di lavoro vicino alla vetrata" },
  { src: "/coworking/dettaglio.jpg", alt: "Dettaglio delle postazioni coworking con mobile rosso" },
  { src: "/coworking/postazione.jpg", alt: "Postazione di lavoro con sedia rossa nella sede treffe" },
];

const SPACES = [
  { icon: Users, title: "Sala riunioni a noleggio", body: "A noleggio per consigli, colloqui, presentazioni e momenti decisionali, con un contesto professionale e riservato." },
  { icon: Wifi, title: "Postazioni coworking", body: "Postazioni a noleggio pronte per lavorare qualche ora o qualche giorno, senza pensare a nulla." },
  { icon: CalendarCheck, title: "Aule a noleggio per corsi", body: "Le nostre aule attrezzate si possono noleggiare per workshop, aggiornamenti, esami e prove pratiche." },
];

const INCLUDED = ["Fibra veloce", "Monitor/TV per presentazioni", "Area break", "Ambiente curato e silenzioso", "Posizione comoda a Frosinone"];

export default function Coworking() {
  return (
    <div data-testid="coworking-page">
      <Seo
        title="Aule a noleggio, coworking e sala riunioni a Frosinone | treffe"
        description="Le aule e gli spazi della sede treffe a Frosinone sono a noleggio: corsi, riunioni, workshop, esami, coworking e domiciliazione. Richiedi disponibilità."
        path="/coworking"
      />
      <PageHeader
        eyebrow="Coworking & aule a noleggio"
        title={<>Le nostre aule a noleggio, anche per il tuo lavoro</>}
        subtitle="Non solo formazione: le aule e gli spazi della nostra sede sono a noleggio per corsi, riunioni, workshop, esami e giornate di lavoro condiviso in un ambiente curato."
      />

      <section className="py-16 md:py-24 bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-red mb-4">Una sede che lavora per te</div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tighter text-brand-black leading-[1.02]">
              Uno spazio serio quando ti serve, senza aprire un ufficio tuo.
            </h2>
            <p className="mt-6 text-brand-ink text-base md:text-lg leading-relaxed max-w-xl">
              Tutte le nostre aule sono a noleggio, anche per poche ore: riunioni operative, coworking, presentazioni, corsi ed esami. Raccontaci cosa devi fare e ti rispondiamo con disponibilità e assetto migliore.
            </p>
            <p className="mt-4 text-brand-black text-base md:text-lg leading-relaxed max-w-xl font-semibold">
              Il modo più rapido è compilare il form: capiamo subito data, orario e numero di persone e ti rispondiamo via email.
            </p>
            <div className="mt-8 mb-14 md:mb-0">
              <Link
                to="/contatti#contatti"
                data-testid="coworking-cta-form"
                className="inline-flex items-center gap-2 bg-brand-red text-white font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-brand-black transition-colors"
              >
                <Mail size={18} /> Richiedi disponibilità
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 md:gap-4" data-testid="coworking-gallery">
            <motion.div
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="row-span-2 overflow-hidden border border-border"
            >
              <img
                src={PHOTOS[1].src}
                alt={PHOTOS[1].alt}
                loading="lazy"
                className="w-full h-full min-h-[280px] md:min-h-[420px] object-cover object-center hover:scale-105 transition-transform duration-700"
              />
            </motion.div>
            {[PHOTOS[2], PHOTOS[4]].map((p, i) => (
              <motion.div
                key={p.src}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 + i * 0.08 }}
                className="overflow-hidden border border-border"
              >
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  className="w-full h-36 sm:h-44 md:h-52 object-cover object-center hover:scale-105 transition-transform duration-700"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-brand-paper border-b border-border" data-testid="coworking-photos">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-3xl mb-10">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-red mb-4">La sede in foto</div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tighter text-brand-black">
              Vieni a vederla dal vivo, intanto guardala qui.
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {PHOTOS.map((p, i) => (
              <motion.div
                key={p.src}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="overflow-hidden border border-border bg-white"
              >
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  className="w-full aspect-[3/4] object-cover object-center hover:scale-105 transition-transform duration-700"
                />
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="aspect-[3/4]"
            >
              <Link
                to="/contatti#contatti"
                data-testid="coworking-gallery-cta"
                className="h-full w-full bg-brand-black text-white flex flex-col items-center justify-center gap-3 p-4 text-center hover:bg-brand-red transition-colors"
              >
                <Mail size={26} />
                <span className="font-heading font-bold text-sm md:text-base leading-tight uppercase tracking-wide">
                  Prenota lo spazio
                </span>
                <span className="text-[11px] text-white/60 uppercase tracking-[0.18em]">Risposta via email</span>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-brand-paper border-b border-border">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-3xl mb-10">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-red mb-4">Spazi a noleggio</div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tighter text-brand-black">
              Tre spazi a noleggio, un'unica sede.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {SPACES.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.article
                  key={s.title}
                  data-testid={`coworking-space-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="bg-white border border-border p-6 hover:border-brand-red transition-colors"
                >
                  <div className="w-12 h-12 mb-5 flex items-center justify-center bg-brand-black text-white">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-brand-black mb-2">{s.title}</h3>
                  <p className="text-sm md:text-base text-brand-ink leading-relaxed">{s.body}</p>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-red mb-4">Cosa trovi</div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tighter text-brand-black">
              Tutto quello che serve per una giornata produttiva.
            </h2>
            <div className="mt-7 grid sm:grid-cols-2 gap-3">
              {INCLUDED.map((item) => (
                <div key={item} className="flex items-center gap-2.5 text-brand-black">
                  <CheckCircle size={17} className="text-brand-red shrink-0" />
                  <span className="text-sm md:text-base">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-brand-black text-white p-7 md:p-9 flex flex-col justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-brand-red mb-3">Come funziona</div>
              <div className="space-y-4">
                {[
                  "Ci scrivi dal form che tipo di incontro o giornata vuoi organizzare.",
                  "Ti rispondiamo via email con disponibilità della sede e assetto consigliato.",
                  "Confermiamo data, orario e dettagli. Tu arrivi, il resto è pronto.",
                ].map((t, i) => (
                  <div key={t} className="flex gap-3">
                    <span className="w-7 h-7 shrink-0 rounded-full border border-brand-red/60 text-brand-red flex items-center justify-center text-xs font-bold">{i + 1}</span>
                    <p className="text-white/75 leading-relaxed text-sm md:text-base">{t}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-8 pt-6 border-t border-white/10 flex items-center gap-2 text-sm text-white/60">
              <MapPin size={16} className="text-brand-red" /> {CONTACT.venueAddress}, {CONTACT.venueCity}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
